// Loan Compliance Module v1.0 PR 3 — TanStack Query hooks for the
// Track 1 / Legal Ops loan-detail surface. Mirrors the
// @/hooks/portfolioAdmin conventions.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  complianceExceptionsApi,
  financialOpsChecksApi,
  legalOpsChecksApi,
  materialEventsApi,
} from "@/api/compliance/client";
import { workQueueKeys } from "./useWorkQueues";
import { loanHealthKeys } from "@/hooks/portfolioAdmin/useLoanHealth";
import { loanCalendarPrefix } from "@/hooks/portfolioAdmin/invalidation";
import type { QueryClient } from "@tanstack/react-query";
import type {
  CaptureMaterialEventRequest,
  ComplianceExceptionDto,
  ComplianceExceptionListQuery,
  EscalateMaterialEventRequest,
  FinancialOpsCheckDto,
  FinancialOpsCheckHistoryDto,
  LegalOpsCheckDto,
  LegalOpsCheckHistoryDto,
  MaterialEventDto,
  MaterialEventNoticeEntryDto,
  ReconcileLoanTapeRequest,
  RecordFinancialOpsCheckRequest,
  RecordLegalOpsCheckRequest,
  ResolveComplianceExceptionRequest,
  RouteMaterialEventNoticeRequest,
  SetMaterialEventDispositionRequest,
  VerifyDscrForPeriodRequest,
} from "@/api/compliance/types";

export const complianceKeys = {
  legalOpsHistory: (loanId: string) =>
    ["compliance", "legal-ops", loanId, "history"] as const,
  financialOpsHistory: (loanId: string) =>
    ["compliance", "financial-ops", loanId, "history"] as const,
  materialEvents: (loanId: string) =>
    ["compliance", "material-events", loanId] as const,
  materialEventNotices: (eventId: string) =>
    ["compliance", "material-events", eventId, "notices"] as const,
  // The positional filter slots below mean exceptions({entityId}) is an
  // EXACT key, not a prefix — invalidations must use this bare prefix to
  // cover every filter combination the cockpit register mounts.
  exceptionsPrefix: ["compliance", "exceptions"] as const,
  exceptions: (query?: ComplianceExceptionListQuery) =>
    [
      "compliance",
      "exceptions",
      query?.scope ?? null,
      query?.entityId ?? null,
      query?.severity ?? null,
      query?.status ?? null,
      query?.track ?? null,
      query?.ownerUserId ?? null,
    ] as const,
};

/** Stale-after-save round 2 — one loan-scoped invalidation for every
 * compliance mutation. Compliance writes feed the legal/financial cadence
 * calendar, the exception register, the compliance work queue, AND loan
 * health (the single severity source for tab badges + the attention feed),
 * so the scope covers all of them; an extra refetch on an unaffected read
 * is harmless, a missed one is minutes of staleness. */
export function invalidateLoanComplianceScope(qc: QueryClient, loanId: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: complianceKeys.legalOpsHistory(loanId) }),
    qc.invalidateQueries({ queryKey: complianceKeys.financialOpsHistory(loanId) }),
    qc.invalidateQueries({ queryKey: complianceKeys.materialEvents(loanId) }),
    qc.invalidateQueries({ queryKey: complianceKeys.exceptionsPrefix }),
    qc.invalidateQueries({ queryKey: loanCalendarPrefix(loanId) }),
    qc.invalidateQueries({ queryKey: loanHealthKeys.health(loanId) }),
    qc.invalidateQueries({ queryKey: workQueueKeys.compliance }),
  ]);
}

// ============================== Legal Ops checks =======================

export function useLegalOpsHistory(loanId: string | undefined) {
  return useQuery<LegalOpsCheckHistoryDto>({
    queryKey: complianceKeys.legalOpsHistory(loanId ?? ""),
    queryFn: () => legalOpsChecksApi.history(loanId!),
    enabled: !!loanId,
  });
}

export function useRecordLegalOpsCheck(loanId: string) {
  const qc = useQueryClient();
  return useMutation<LegalOpsCheckDto, unknown, RecordLegalOpsCheckRequest>({
    mutationFn: (body) => legalOpsChecksApi.record(loanId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

// ============================== Financial Ops checks ===================

export function useFinancialOpsHistory(loanId: string | undefined) {
  return useQuery<FinancialOpsCheckHistoryDto>({
    queryKey: complianceKeys.financialOpsHistory(loanId ?? ""),
    queryFn: () => financialOpsChecksApi.history(loanId!),
    enabled: !!loanId,
  });
}

export function useRecordFinancialOpsCheck(loanId: string) {
  const qc = useQueryClient();
  return useMutation<
    FinancialOpsCheckDto,
    unknown,
    RecordFinancialOpsCheckRequest
  >({
    mutationFn: (body) => financialOpsChecksApi.record(loanId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

export function useVerifyDscrForPeriod(loanId: string) {
  const qc = useQueryClient();
  return useMutation<FinancialOpsCheckDto, unknown, VerifyDscrForPeriodRequest>({
    mutationFn: (body) => financialOpsChecksApi.verifyDscr(loanId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

export function useReconcileLoanTape(loanId: string) {
  const qc = useQueryClient();
  return useMutation<FinancialOpsCheckDto, unknown, ReconcileLoanTapeRequest>({
    mutationFn: (body) => financialOpsChecksApi.reconcileLoanTape(loanId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

// ============================== Material events ========================

export function useMaterialEvents(loanId: string | undefined) {
  return useQuery<MaterialEventDto[]>({
    queryKey: complianceKeys.materialEvents(loanId ?? ""),
    queryFn: () => materialEventsApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useCaptureMaterialEvent(loanId: string) {
  const qc = useQueryClient();
  return useMutation<MaterialEventDto, unknown, CaptureMaterialEventRequest>({
    mutationFn: (body) => materialEventsApi.capture(loanId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

export function useMaterialEventNotices(eventId: string | undefined) {
  return useQuery<MaterialEventNoticeEntryDto[]>({
    queryKey: complianceKeys.materialEventNotices(eventId ?? ""),
    queryFn: () => materialEventsApi.notices(eventId!),
    enabled: !!eventId,
  });
}

export function useRouteMaterialEventNotice(loanId: string, eventId: string) {
  const qc = useQueryClient();
  return useMutation<
    MaterialEventNoticeEntryDto,
    unknown,
    RouteMaterialEventNoticeRequest
  >({
    mutationFn: (body) => materialEventsApi.routeNotice(eventId, body),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({
          queryKey: complianceKeys.materialEventNotices(eventId),
        }),
        invalidateLoanComplianceScope(qc, loanId),
      ]),
  });
}

export function useSetMaterialEventDisposition(
  loanId: string,
  eventId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    MaterialEventDto,
    unknown,
    SetMaterialEventDispositionRequest
  >({
    mutationFn: (body) => materialEventsApi.setDisposition(eventId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

export function useEscalateMaterialEvent(loanId: string, eventId: string) {
  const qc = useQueryClient();
  return useMutation<MaterialEventDto, unknown, EscalateMaterialEventRequest>({
    mutationFn: (body) => materialEventsApi.escalate(eventId, body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

export function useLinkMaterialEventLoanEvent(
  loanId: string,
  eventId: string,
) {
  const qc = useQueryClient();
  return useMutation<MaterialEventDto, unknown, string>({
    mutationFn: (loanEventId) =>
      materialEventsApi.linkLoanEvent(eventId, { loanEventId }),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}

// ============================== Exceptions =============================

export function useComplianceExceptions(query?: ComplianceExceptionListQuery) {
  return useQuery<ComplianceExceptionDto[]>({
    queryKey: complianceKeys.exceptions(query),
    queryFn: () => complianceExceptionsApi.list(query),
  });
}

export function useResolveComplianceException(loanId: string) {
  const qc = useQueryClient();
  return useMutation<
    ComplianceExceptionDto,
    unknown,
    { id: string; body: ResolveComplianceExceptionRequest }
  >({
    mutationFn: (args) =>
      complianceExceptionsApi.resolve(args.id, args.body),
    onSuccess: () => invalidateLoanComplianceScope(qc, loanId),
  });
}
